import React from "react";
import Button from "../../../components/ui/Button";
import Icon from "../../../components/AppIcon";

const OrdersTable = ({
  orders,
  meta,
  language = "ar",
  onEdit,
  onDelete,
  onView,
  onEditStatus,
  onPageChange,
}) => {

  const {
    current_page = 1,
    last_page = 1,
    from = 0,
    to = 0,
    total = 0,
    prev_page_url = null,
    next_page_url = null,
  } = meta || {};

  const pages = Array.from({ length: last_page }, (_, i) => i + 1);

  /* ================== LABELS ================== */
  const labels = {
    orderId: { en: "Order ID", ar: "رقم الطلب" },
    customer: { en: "Customer", ar: "العميل" },
    phone: { en: "Phone", ar: "الهاتف" },
    items: { en: "Items", ar: "العناصر" },
    area: { en: "Area", ar: "المنطقة" },
    total: { en: "Total", ar: "المجموع" },
    status: { en: "Status", ar: "الحالة" },
    date: { en: "Date", ar: "التاريخ" },
    actions: { en: "Actions", ar: "الإجراءات" },

    CREATED: { en: "Created", ar: "تم الإنشاء" },
    IN_PROGRESS: { en: "In Progress", ar: "قيد التنفيذ" },
    COMPLETED: { en: "Completed", ar: "مكتمل" },
    CANCELLED: { en: "Cancelled", ar: "ملغي" },
  };

  const getStatusConfig = (status) => {
    const map = {
      1: {
        key: "CREATED",
        label: labels.CREATED[language],
        icon: "Clock",
        bgColor: "bg-warning/10",
        textColor: "text-warning",
        borderColor: "border-warning/20",
      },

      2: {
        key: "PREPARED",
        label: labels.PREPARED?.[language] || "Prepared",
        icon: "Loader",
        bgColor: "bg-primary/10",
        textColor: "text-primary",
        borderColor: "border-primary/20",
      },

      3: {
        key: "ON_DELIVERY",
        label: labels.ON_DELIVERY?.[language] || "On Delivery",
        icon: "Truck",
        bgColor: "bg-accent/10",
        textColor: "text-accent",
        borderColor: "border-accent/20",
      },

      4: {
        key: "DELIVERED",
        label: labels.DELIVERED?.[language] || "Delivered",
        icon: "CheckCircle",
        bgColor: "bg-success/10",
        textColor: "text-success",
        borderColor: "border-success/20",
      },

      5: {
        key: "COMPLETED",
        label: labels.COMPLETED[language],
        icon: "CheckCheck",
        bgColor: "bg-success/20",
        textColor: "text-success",
        borderColor: "border-success/30",
      },

      6: {
        key: "CANCELLED",
        label: labels.CANCELLED[language],
        icon: "XCircle",
        bgColor: "bg-error/10",
        textColor: "text-error",
        borderColor: "border-error/20",
      },
    };

    return (
      map?.[status] || {
        key: "UNKNOWN",
        label: "Unknown",
        icon: "HelpCircle",
        bgColor: "bg-muted",
        textColor: "text-muted-foreground",
        borderColor: "border-border",
      }
    );
  };

  return (
    <div className="bg-card rounded-xl border border-border overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[1100px] text-center">
          <thead className="bg-muted border-b">
            <tr>
              <th className="px-6 py-4">{labels.orderId[language]}</th>
              <th className="px-6 py-4">{labels.customer[language]}</th>
              <th className="px-6 py-4">{labels.phone[language]}</th>
              <th className="px-6 py-4">{labels.items[language]}</th>
              <th className="px-6 py-4">{labels.area[language]}</th>
              <th className="px-6 py-4">{labels.total[language]}</th>
              <th className="px-6 py-4">{labels.status[language]}</th>
              <th className="px-6 py-4">{labels.date[language]}</th>
              <th className="px-6 py-4">{labels.actions[language]}</th>
            </tr>
          </thead>

          <tbody className="divide-y">
            {orders?.map((order) => {
              const statusConfig = getStatusConfig(order?.order_status);

              return (
                <tr
                  key={order.id}
                  className="hover:bg-muted/40 transition-smooth"
                >
                  <td className="px-6 py-4">#{order.id}</td>

                  <td className="px-6 py-4">
                    {order.first_name} {order.last_name}
                  </td>

                  <td className="px-6 py-4 text-text-secondary">
                    {order.phone_number}
                  </td>

                  <td className="px-6 py-4">{order.items?.length || 0}</td>

                  <td className="px-6 py-4">
                    {order.area?.name}
                    <div className="text-xs text-text-secondary">
                      {order.area?.governorate_name}
                    </div>
                  </td>

                  <td className="px-6 py-4 font-semibold text-primary">
                    {order.total_amount}
                  </td>

                  {/* ✅ STATUS */}
                  <td className="px-4 py-3">
                    <div
                      className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border text-xs
            ${statusConfig.bgColor}
            ${statusConfig.textColor}
            ${statusConfig.borderColor}
          `}
                    >
                      <Icon name={statusConfig.icon} size={14} />
                      {statusConfig.label}
                    </div>
                  </td>

                  <td className="px-6 py-4 text-text-secondary">
                    {new Date(order.order_date).toLocaleDateString()}
                  </td>

                  <td className="px-6 py-4">
                    <div className="flex justify-center gap-2">
                      <button
                        onClick={() => onEditStatus(order)}
                        className="p-2 rounded-lg hover:bg-primary/10 text-primary transition"
                      >
                        <Icon name="RefreshCcw" size={16} />
                      </button>

                      <button
                        onClick={() => onView(order)}
                        className="p-2 rounded-lg hover:bg-muted text-foreground transition"
                      >
                        <Icon name="Eye" size={16} />
                      </button>

                      <button
                        onClick={() => onEdit(order)}
                        className="p-2 rounded-lg hover:bg-primary/10 text-primary transition"
                      >
                        <Icon name="Edit2" size={16} />
                      </button>

                      <button
                        onClick={() => onDelete(order)}
                        className="p-2 rounded-lg hover:bg-error/10 text-error transition"
                      >
                        <Icon name="Trash2" size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* PAGINATION – SAME AS USER TABLE */}
      <div className="flex flex-col sm:flex-row justify-between items-center p-4 bg-muted/30 gap-3">
        <span className="text-sm text-text-secondary">
          {from}-{to} من {total}
        </span>

        <div className="flex gap-1 flex-wrap">
          <Button
            size="sm"
            variant="outline"
            disabled={!prev_page_url}
            onClick={() => onPageChange(current_page - 1)}
          >
            السابق
          </Button>

          {pages.map((page) => (
            <Button
              key={page}
              size="sm"
              variant={page === current_page ? "default" : "outline"}
              onClick={() => onPageChange(page)}
            >
              {page}
            </Button>
          ))}

          <Button
            size="sm"
            variant="outline"
            disabled={!next_page_url}
            onClick={() => onPageChange(current_page + 1)}
          >
            التالي
          </Button>
        </div>
      </div>
    </div>
  );
};

export default OrdersTable;
